import {FoundParameter} from '@/components/estimates/expertEstimate/foundValues';
import {ExpertEstimateState} from '@/model/api';
import {ParameterValues} from '@/model/api/parameters';
import {HttpClient} from '@/model/http';
import {isLeft} from 'fp-ts/lib/Either';

/**
 * The values the AI found in the sources of an expert estimate
 */
export default class EstimateFoundValues {
    private static readonly HEADERS = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    constructor(
        private readonly estimateId: string,
        private readonly http: HttpClient,
    ) {
    }

    public async load(): Promise<FoundParameter[]> {
        const response = await this.http.safeRequest<{parameters: FoundParameter[]}>(
            this.url(),
            {method: 'GET', headers: {'Accept': 'application/json'}},
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right.parameters;
    }

    /**
     * Saves the picked values to the form, the required parameters may still be empty; returns the saved estimate
     */
    public async takeOver(values: ParameterValues): Promise<ExpertEstimateState> {
        const response = await this.http.safeRequest<ExpertEstimateState>(this.url(), {
            method: 'POST',
            headers: EstimateFoundValues.HEADERS,
            body: JSON.stringify({values}),
        });

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    /**
     * The values of these sources are no longer offered as new
     */
    public async markAsSeen(sourceIds: string[]): Promise<void> {
        const response = await this.http.safeRequest<null>(this.url(), {
            method: 'PUT',
            headers: EstimateFoundValues.HEADERS,
            body: JSON.stringify({sourceIds}),
        });

        if (isLeft(response)) {
            throw response.left;
        }
    }

    private url(): string {
        return `/api/expert-estimate/estimates/${this.estimateId}/found-values`;
    }
}
